/**
 * Guarantors Module - TanStack Query Hooks
 *
 * This module provides all guarantor-related hooks for:
 * - Creating guarantors
 * - Updating guarantors
 *
 * Guarantors are typically associated with tenant reports and contracts,
 * so mutations will invalidate related caches.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Guarantor } from "js/components/types";

import { createGuarantor, updateGuarantor } from "../api/guarantors";
import { toast } from "../utils/toast";
import { queryKeys } from "./queryKeys";

// ============================================================================
// MUTATION HOOKS - CREATE, UPDATE Operations
// ============================================================================

/**
 * Hook to create a new guarantor
 *
 * Invalidates:
 * - guarantors list
 * - tenant reports (guarantor is associated with a report)
 */
export const useCreateGuarantor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newGuarantor: Guarantor) => createGuarantor(newGuarantor),
    onSuccess: () => {
      toast.success("Aval creado exitosamente");

      // Invalidate guarantors lists
      queryClient.invalidateQueries({ queryKey: queryKeys.guarantors.lists() });

      // Invalidate tenant reports (guarantor is linked to a report)
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantReports.all });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al crear el aval";
      toast.error(errorMessage);
    },
  });
};

/**
 * Hook to update an existing guarantor
 *
 * Invalidates:
 * - guarantors list
 * - specific guarantor detail
 * - tenant reports (guarantor data may have changed)
 */
export const useUpdateGuarantor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      guarantor,
      guarantorId,
      queryParams,
    }: {
      guarantor: Guarantor;
      guarantorId: string;
      queryParams?: object;
    }) => updateGuarantor(guarantor, guarantorId, queryParams),
    onSuccess: (_, variables) => {
      toast.success("Aval actualizado exitosamente");

      // Invalidate guarantors lists
      queryClient.invalidateQueries({ queryKey: queryKeys.guarantors.lists() });

      // Invalidate specific guarantor
      queryClient.invalidateQueries({
        queryKey: queryKeys.guarantors.detail(variables.guarantorId),
      });

      // Invalidate tenant reports (guarantor is linked to a report)
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantReports.all });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al actualizar el aval";
      toast.error(errorMessage);
    },
  });
};

// ============================================================================
// EXPORTS
// ============================================================================

// Export all hooks as named exports
export default {
  useCreateGuarantor,
  useUpdateGuarantor,
};
